import { coolerConfigFor } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import { LenderCrossPoolMeta, UserData } from '../utils/types'
import { COOLER_USER_CALLS } from './userCallBuild'

/** Per-position Cooler detail attached to the debt row (raw wad strings). */
export interface CoolerPositionInfo {
  /** Contract-computed health vs LLTV (1e18 = at the threshold). This is the
   *  AUTHORITATIVE liquidation signal — there is NO price trigger, so never
   *  derive one from a market price (the Frankencoin lesson). */
  healthFactor: string
  /** Current LTV as a PRICE: debt wad per gOHM token (not a ratio). */
  currentLtv: string
  /** gOHM delegated out via the DLGTE module (wad). Withdrawing delegated
   *  collateral reverts without undelegation requests first. */
  totalDelegated: string
  /** Max debt at the current OLTV for the current collateral (wad). */
  maxOriginationDebtAmount: string
  /** Debt at which the position becomes liquidatable (LLTV, wad). */
  liquidationDebtAmount: string
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/**
 * Returns a [converter, expectedCallCount] tuple for Cooler user data.
 *
 * ONE `accountPosition(account)` call (see `buildCoolerUserCall`) parsed
 * into a single sub-account `'0'` — positions are address-keyed, at most
 * one per user. Emits the collateral balance on the gOHM market uid and
 * the live debt on the loan uid, plus a `coolerInfo` extra carrying the
 * contract's own health/LTV/delegation numbers. Cooler is NOT multi-market,
 * so this returns the `UserData` entry directly (Aave pattern), not a
 * per-market map.
 *
 * All struct fields are WAD regardless of the debt token — parse with 18
 * decimals without exception.
 */
export const getCoolerUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  const cfg = coolerConfigFor(lender, chainId)
  const expected = cfg?.monoCooler ? COOLER_USER_CALLS : 0

  return [
    (data: any[]) => {
      if (!cfg || expected === 0 || !meta) return undefined
      if (!data || data.length < expected) return undefined

      const raw = data[0]
      // A failed read is NOT an empty position — publish nothing rather than
      // reporting the user as debt-free.
      if (isFailedCall(raw)) return undefined

      // Single-tuple output: viem decodes to a named object; tolerate a
      // positional array from other decoders.
      const pos = Array.isArray(raw)
        ? {
            collateral: raw[0],
            currentDebt: raw[1],
            maxOriginationDebtAmount: raw[2],
            liquidationDebtAmount: raw[3],
            healthFactor: raw[4],
            currentLtv: raw[5],
            totalDelegated: raw[6],
          }
        : raw
      if (pos === null || typeof pos !== 'object') return undefined

      const collateral = big(pos.collateral)
      const debt = big(pos.currentDebt)
      if (collateral === 0n && debt === 0n) return undefined

      const collAddr = cfg.gohm.toLowerCase()
      const collUid = createMarketUid(chainId, lender, collAddr)
      // The debt token is governance-swappable and the LIVE address decides
      // the public-data uid — prefer the meta row that is not the collateral
      // over the seed's usds, so the join survives a swap.
      let loanUid = createMarketUid(chainId, lender, cfg.usds.toLowerCase())
      if (!meta[loanUid]) {
        const other = Object.keys(meta).find((k) => k !== collUid)
        if (other) loanUid = other
      }
      const collMeta = meta[collUid]
      const loanMeta = meta[loanUid]

      const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
      const collOracle = collMeta ? getOraclePrice(collMeta) : 0
      const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
      const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
      const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
      const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay

      const coolerInfo: CoolerPositionInfo = {
        healthFactor: big(pos.healthFactor).toString(),
        currentLtv: big(pos.currentLtv).toString(),
        totalDelegated: big(pos.totalDelegated).toString(),
        maxOriginationDebtAmount: big(pos.maxOriginationDebtAmount).toString(),
        liquidationDebtAmount: big(pos.liquidationDebtAmount).toString(),
      }

      // WAD everywhere — 18 decimals regardless of token decimals.
      const collStr = parseRawAmount(collateral.toString(), 18)
      const collNum = Number(collStr)
      const debtStr = parseRawAmount(debt.toString(), 18)
      const debtNum = Number(debtStr)

      const loanUnderlying = loanMeta?.asset?.address ?? cfg.usds.toLowerCase()

      const lendingPositions: Record<string, any> = {
        '0': {
          [collUid]: {
            marketUid: collUid,
            underlying: collAddr,
            deposits: collStr,
            debt: '0',
            debtStable: '0',
            depositsUSD: collNum * collDisplay,
            debtUSD: 0,
            debtStableUSD: 0,
            depositsUSDOracle: collNum * collOracle,
            debtUSDOracle: 0,
            debtStableUSDOracle: 0,
            stableBorrowRate: '0',
            collateralEnabled: true,
            claimableRewards: 0,
          },
          [loanUid]: {
            marketUid: loanUid,
            underlying: loanUnderlying,
            deposits: '0',
            debt: debtStr,
            debtStable: '0',
            depositsUSD: 0,
            debtUSD: debtNum * loanDisplay,
            debtStableUSD: 0,
            depositsUSDOracle: 0,
            debtUSDOracle: debtNum * loanOracle,
            debtStableUSDOracle: 0,
            stableBorrowRate: '0',
            collateralEnabled: false,
            claimableRewards: 0,
            coolerInfo,
          },
        },
      }
      const modes: Record<string, number> = { '0': 0 }
      const hist = {
        '0': {
          totalDeposits24h: collNum * collHist,
          totalDebt24h: debtNum * loanHist,
        },
      }

      const userData = createMultiAccountTypeUserState(
        { chainId, account, lendingPositions, modes } as any,
        meta,
        hist,
      )
      return userData && userData.data.length > 0 ? userData : undefined
    },
    expected,
  ]
}
